import { Component, Input, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { SearchApi } from '../../../api/search-api';
import { InputConverter } from '../../../inputConverter';
import { AoiService } from '../../../services/aoi.service';
import { BaseComponent } from '../../base.component';

import AssetConfiguration, { AssetConfigurationArrayConverter, IAssetSearchProps } from './asset-search.config';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-asset-search',
    styleUrls: ['../../sharedStyles/base.scss', './asset-search.component.scss'],
    templateUrl: './asset-search.component.html'
})
export class AssetSearchComponent extends BaseComponent {
    @Input()
    @InputConverter(AssetConfigurationArrayConverter)
    config: AssetConfiguration[];

    @Input()
    set properties(value: string) {
        this._properties = value;
        this.properties.split(',').forEach(propName => {
            for (const prop of this.config) {
                if (propName === prop.propName) {
                    this.propertiesArray.push(prop);
                }
            }
        });
    }

    get properties() {
        return this._properties;
    }
    _properties;

    searchText = '';
    searchUpdated = new Subject<string>();
    propertiesArray: IAssetSearchProps[] = [];
    showResults = false;
    idCount: number = null;

    searchCriteria: any;
    isActive: boolean;

    constructor(
        _vcr: ViewContainerRef,
        private readonly aoiService: AoiService,
        private readonly searchApi: SearchApi
    ) { super(_vcr, true); }

    @Input()
    public activate = () => {
        if (this.isActive) { return; }

        const debounceTimer = 500;
        this.searchUpdated
            .pipe(
                debounceTime(debounceTimer),
                distinctUntilChanged()
            )
            .subscribe(searchText => {
                let validSearch = true;
                const endChar = searchText.substr(-1);
                if (endChar === ' ' || endChar === ',') {
                    validSearch = false;
                }
                if (searchText.length !== 0 && validSearch) {
                    this.createQuery(searchText);
                } else if (searchText.length === 0) {
                    this.clearResults();
                }
            });

        this.isActive = true;
    }

    @Input()
    public reset = () => {
        this.searchText = '';
        this.searchCriteria = null;
        this.clearResults();
    }

    createQuery(searchText: string) {
        let queryString = '';
        for (const prop of this.propertiesArray) {
            if (prop.idType !== '$intId' || this.hasAllIntValues()) {
                queryString += `
                ${prop.propName}: layers(id: ${prop.layerId}) {
                    data(
                        fieldNames: ["${prop.columnName}"], 
                        query: {
                            field: "${prop.columnName}", 
                            operator: "IN",
                            value:  $stringIds
                        } 
                    )
                }
            `;
            }
        }
        this.getResults(queryString, searchText);
    }

    getResults(queryString, stringIds) {
        this.searchApi.getAssetIds(queryString, stringIds).then(results => {
            if (results) {
                this.assignDataToProperties(results);
            }
        });
    }

    assignDataToProperties(results) {
        this.clearResults();
        for (const prop of this.propertiesArray) {
            if (results[prop.propName] && results[prop.propName].length > 0) {
                for (const x of results[prop.propName]) {
                    prop.data = [...new Set(prop.data.concat(x.data.flat()))];
                }
            }
        }
        this.showResults = true;
    }

    clearResults() {
        for (const prop of this.propertiesArray) {
            prop.data = [];
        }
        this.showResults = false;
    }

    async selectResults(result) {
        if (result.data.length === 0) {
            return;
        }

        this.searchCriteria = {
            type: 'assetId',
            title: `${result.displayName}: ${result.data[0]}`,
            layerId: result.layerId,
            columnName: result.columnName,
            data: String(result.data)
        };
    }

    hasAllIntValues() {
        const values = (<any>this.searchText).split(',').map(x => x.trim());
        return values.filter(x => !isNaN(parseInt(x, 10)) && isFinite(x)).length === values.length;
    }

    @Input()
    public addResultToMap = () => {
        this.aoiService.setSearchCriterias([this.searchCriteria]);
    }
}
